/**
 * Computes the visibility state for cart sheet panels and their inner progress indicators.
 *
 * @param {Object} options
 * @param {number}  options.groceryItemsTotalCost - Total cost of grocery items.
 * @param {boolean} options.hideWineCartSheet - Whether the wine sheet is hidden (e.g. auto-dismissed).
 * @param {boolean} options.hideGroceryCartSheet - Whether the grocery sheet is hidden (e.g. auto-dismissed).
 * @param {boolean} options.hideFrozenToast - Whether the frozen fee waive toast is hidden (e.g. auto-dismissed).
 * @param {boolean} options.isFrozenAisleCartSheet - Whether the shopper is on the frozen PLP; mounts grocery sheet for aisle-specific UI.
 * @param {boolean} options.productAdded - Whether a product has been added to the cart.
 * @param {boolean} options.showOrderMinProgress - Whether the order minimum progress bar should be shown.
 * @param {boolean} options.showFreeShippingProgress - Whether the free shipping progress bar should be shown.
 * @param {boolean} options.showFrozenFeeWaiveToast - Whether the frozen fee waive toast should be shown.
 * @param {number}  options.totalWineBottleQty - Total number of wine bottles in the cart.
 * @returns {{ showWineSheet: boolean, showGrocerySheet: boolean, grocerySheet: { orderMinProgress: boolean, freeShippingProgress: boolean, frozenFeeWaive: boolean } }} showWineSheet when wine bottles in cart, product was added, and the wine sheet is not hidden. showGrocerySheet when cart rules show the grocery panel or isFrozenAisleCartSheet mounts the frozen-aisle variant.
 */
export function getCartSheetVisibility({
    groceryItemsTotalCost,
    hideFrozenToast,
    hideGroceryCartSheet,
    hideWineCartSheet,
    isFrozenAisleCartSheet = false,
    productAdded,
    showFreeShippingProgress,
    showFrozenFeeWaiveToast,
    showOrderMinProgress,
    totalWineBottleQty,
}) {
    const showWineSheet = !hideWineCartSheet && totalWineBottleQty > 0 && productAdded;

    const orderMinProgress = showOrderMinProgress && !hideGroceryCartSheet;
    const freeShippingProgress = showFreeShippingProgress && !hideGroceryCartSheet;
    const frozenFeeWaive = showFrozenFeeWaiveToast && !hideFrozenToast;

    const showGrocerySheet =
        isFrozenAisleCartSheet ||
        (productAdded &&
            groceryItemsTotalCost > 0 &&
            (orderMinProgress || freeShippingProgress || frozenFeeWaive) &&
            (!hideGroceryCartSheet || frozenFeeWaive));

    return {
        showWineSheet,
        showGrocerySheet,
        grocerySheet: {
            orderMinProgress,
            freeShippingProgress,
            frozenFeeWaive,
        },
    };
}
